import * as React from "react";

/**
 * useModalKeys — global keyboard contract for in-app modals (spec 17 §4.1):
 *
 *   • Esc       → onEscape (host decides whether to dirty-warn first)
 *   • ⌘/Ctrl+↵  → onSubmit  (works regardless of focused element, including
 *                            inside textareas where bare Enter is a newline)
 *
 * The hook attaches `keydown` to the document only while `enabled` is true,
 * so unmounted / closed modals don't intercept keys.
 *
 * Why not bind to the form's `onKeyDown`? Modals frequently render the
 * close button outside the `<form>` (header X, overlay click, etc.); a
 * document-level handler covers every focus location in the modal.
 */
export function useModalKeys({
  enabled,
  onEscape,
  onSubmit,
}: {
  enabled: boolean;
  onEscape: () => void;
  onSubmit?: () => void;
}): void {
  React.useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onEscape();
        return;
      }
      // Cmd on macOS, Ctrl elsewhere. Both Enter codes (Enter, NumpadEnter)
      // come through as `Enter`.
      if (
        onSubmit &&
        e.key === "Enter" &&
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }
    };

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [enabled, onEscape, onSubmit]);
}
